import React from 'react';
import classNames from 'classnames';
import { Field } from 'react-final-form';

import css from './FieldCheckbox.module.css';
import { iconOfferingArr } from '../../util/data';

/**
 * IconCheckbox
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className add more style rules in addition to components own css.root
 * @param {string?} props.checkedClassName overwrite components own css.checked
 * @param {string?} props.boxClassName overwrite components own css.box
 * @param {boolean} props.checked whether the checkbox is checked
 * @returns {JSX.Element} checkbox svg that replaces the native checkbox
 */
const IconCheckbox = ({ className, checkedClassName, boxClassName, checked }) => {
  return (
    <svg
    className={className}
    width="32"
    height="32"
    viewBox="0 0 24 24"  // Add this line
    xmlns="http://www.w3.org/2000/svg"
  >
    <g fill="none" fillRule="evenodd">
      <g transform="translate(2 2)">
        <path
          className={checked ? (checkedClassName || css.checked) : (boxClassName || css.box)}
          d="M9.9992985 1.5048549l-.0194517 6.9993137C9.977549 9.3309651 9.3066522 10 8.4798526 10H1.5001008c-.8284271 0-1.5-.6715729-1.5-1.5l-.000121-7c0-.8284271.6715728-1.5 1.5-1.5h.000121l6.9993246.0006862c.8284272.000067 1.4999458.671694 1.499879 1.5001211a1.5002208 1.5002208 0 0 1-.0000059.0040476z"
          fill={checked ? '#FFF' : 'none'}
          strokeWidth="2"
        />
      </g>
      {checked && (
        <path
          d="M5.636621 10.7824771L3.3573694 8.6447948c-.4764924-.4739011-.4764924-1.2418639 0-1.7181952.4777142-.473901 1.251098-.473901 1.7288122 0l1.260291 1.1254782 2.8256927-4.5462307c.3934117-.5431636 1.1545778-.6695372 1.7055985-.278265.5473554.3912721.6731983 1.150729.2797866 1.6951077l-3.6650524 5.709111c-.2199195.306213-.5803433.5067097-.9920816.5067097-.3225487 0-.6328797-.1263736-.8637952-.3560334z"
          fill="#FFF"
        />
      )}
    </g>
  </svg>
  
  );
};

/**
 * Final Form Field containing checkbox input
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className add more style rules in addition to components own css.root
 * @param {string?} props.rootClassName overwrite components own css.root
 * @param {string?} props.svgClassName is passed to checkbox svg as className
 * @param {string?} props.textClassName overwrite components own css.textRoot given to label
 * @param {string} props.id unique ID for the checkbox input (must be unique across all checkboxes)
 * @param {string} props.name groups checkboxes into an array of selected values
 * @param {string} props.value unique value for this checkbox in the group
 * @param {ReactNode} props.label
 * @param {boolean} props.isMap whether to use a custom icon from iconOfferingArr
 * @param {boolean} props.useSuccessColor whether to use success color variant
 * @returns {JSX.Element} Final Form Field containing checkbox input
 */
const FieldCheckbox = props => {
  const {
    rootClassName,
    className,
    svgClassName,
    textClassName,
    id,
    label,
    value,
    isMap,
    useSuccessColor,
    ...rest
  } = props;
  const classes = classNames(rootClassName || css.root, className);

  // Workaround for Firefox & react-final-form bug: https://github.com/final-form/react-final-form/issues/134
  const handleOnChange = (input, event) => {
    const { onBlur, onChange } = input;
    onChange(event);
    onBlur(event);

    if (rest.onChange) {
      rest.onChange(event);
    }
  };

  const successColorVariantMaybe = useSuccessColor
    ? {
        checkedClassName: css.checkedSuccess,
        boxClassName: css.boxSuccess,
      }
    : {};
  const disabledColorMaybe = rest.disabled
    ? {
        checkedClassName: css.checkedDisabled,
        boxClassName: css.boxDisabled,
      }
    : {};

  return (
    <span className={classes}>
      <Field type="checkbox" name={props.name} value={value} {...rest}>
        {({ input, disabled }) => {
          const CustomIcon = isMap ? iconOfferingArr.find(item => item.key === value)?.icon : null;

          return (
            <label htmlFor={id} className={css.label}>
              <input
                id={id}
                className={css.input}
                type="checkbox"
                {...input}
                onChange={event => handleOnChange(input, event)}
                disabled={disabled}
              />
              <span className={css.checkboxWrapper}>
                {isMap && CustomIcon ? (
                  // Pass checked state and className to custom icon
                  React.cloneElement(CustomIcon, {
                    className: classNames(svgClassName, { [css.checked]: input.checked }),
                    checked: input.checked,
                  })
                ) : (
                  <IconCheckbox
                    className={classNames(svgClassName, { [css.checked]: input.checked })}
                    checked={input.checked}
                    {...successColorVariantMaybe}
                    {...disabledColorMaybe}
                  />
                )}
              </span>
              <span className={classNames(css.text, textClassName || css.textRoot)}>{label}</span>
            </label>
          );
        }}
      </Field>
    </span>
  );
};

export default FieldCheckbox;